import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  invalidatePublicConfigCache,
  projectPublicConfigGoogleIntegrationsOff,
  type PublicConfig,
} from "./public-config";
import { onGoogleIntegrationsRuntimeBoundary } from "@/shared/google/google-integration-runtime.mjs";

const PUBLIC_CONFIG_QUERY_KEY = ["public-config"] as const;
const GOOGLE_INTEGRATION_QUERY_PREFIXES = [["calendar-status"], ["calendar-agenda"]] as const;

type ForegroundRefreshTargets = {
  documentTarget?: Pick<Document, "addEventListener" | "removeEventListener" | "visibilityState">;
  windowTarget?: Pick<Window, "addEventListener" | "removeEventListener"> & {
    TB_PUBLIC_CONFIG?: PublicConfig;
  };
};

export function purgeGoogleIntegrationQueryCache(queryClient: QueryClient): void {
  for (const queryKey of GOOGLE_INTEGRATION_QUERY_PREFIXES) {
    queryClient.removeQueries({ queryKey });
  }
}

export function installPublicConfigForegroundRefresh(
  queryClient: QueryClient,
  targets: ForegroundRefreshTargets = {},
) {
  const documentTarget = targets.documentTarget || document;
  const windowTarget =
    targets.windowTarget ||
    (window as Window & {
      TB_PUBLIC_CONFIG?: PublicConfig;
    });
  let refreshInFlight: Promise<unknown> | null = null;
  const stopRuntimeBoundary = onGoogleIntegrationsRuntimeBoundary(() =>
    purgeGoogleIntegrationQueryCache(queryClient),
  );
  const refreshFailClosed = () => {
    if (documentTarget.visibilityState !== "visible" || refreshInFlight) return;
    queryClient.setQueryData<PublicConfig>(PUBLIC_CONFIG_QUERY_KEY, (current) =>
      current ? projectPublicConfigGoogleIntegrationsOff(current) : current,
    );
    const closed = queryClient.getQueryData<PublicConfig>(PUBLIC_CONFIG_QUERY_KEY);
    const existing = closed || windowTarget.TB_PUBLIC_CONFIG;
    if (existing) {
      windowTarget.TB_PUBLIC_CONFIG = projectPublicConfigGoogleIntegrationsOff(existing);
    }
    invalidatePublicConfigCache();
    const pending = queryClient
      .refetchQueries(
        { queryKey: PUBLIC_CONFIG_QUERY_KEY, exact: true, type: "active" },
        { cancelRefetch: true },
      )
      .catch(() => undefined)
      .finally(() => {
        if (refreshInFlight === pending) refreshInFlight = null;
      });
    refreshInFlight = pending;
  };
  const refreshWhenVisible = () => {
    if (documentTarget.visibilityState === "visible") refreshFailClosed();
  };

  windowTarget.addEventListener("focus", refreshFailClosed);
  documentTarget.addEventListener("visibilitychange", refreshWhenVisible);
  return () => {
    stopRuntimeBoundary();
    windowTarget.removeEventListener("focus", refreshFailClosed);
    documentTarget.removeEventListener("visibilitychange", refreshWhenVisible);
  };
}

/**
 * Revalidates the operator switch for every signed-in and signed-out route.
 * Foregrounding first closes the cached Google surface synchronously; only a
 * successful exact-query refetch can publish ON again.
 */
export function PublicConfigForegroundRefresh() {
  const queryClient = useQueryClient();
  useEffect(() => installPublicConfigForegroundRefresh(queryClient), [queryClient]);
  return null;
}
